import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Plus, FileText, MagnifyingGlass, Trash, FilePdf, Eye,
  FileDoc, LockSimple, ClockCounterClockwise, SealCheck,
  ArrowCounterClockwise, MagicWand,
} from '@phosphor-icons/react';
import { STATUS_CONFIG } from '../DocumentShared';

const DOC_TYPES = [
  { value: 'pgc', label: 'PGC', desc: 'Plan General de Coordination' },
  { value: 'ppsps', label: 'PPSPS', desc: 'Plan de Prevention' },
  { value: 'diuo', label: 'DIUO', desc: 'Dossier Interventions Ulterieures' },
  { value: 'icp', label: 'ICP', desc: 'Inspection Commune Prealable' },
];

export function DocumentCardList({
  documents, chantiers, getChantierName,
  searchTerm, setSearchTerm,
  filterType, setFilterType,
  filterChantier, setFilterChantier,
  filterStatus, setFilterStatus,
  onOpenWizard, onOpenView, onValidate, onNewVersion,
  onDownloadPdf, onDownloadDocx, onSendEmail, onDeleteConfirm,
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-base font-semibold text-[#111827]" data-testid="section-gestion">Gestion avancee des documents</h2>
        <Button onClick={onOpenWizard} disabled={chantiers.length === 0}
          className="bg-[#FF6B00] hover:bg-orange-700 h-10 px-4 text-sm flex-shrink-0"
          data-testid="new-document-btn">
          <Plus size={18} className="mr-1" />
          <span className="hidden sm:inline">Nouveau document</span>
          <span className="sm:hidden">Nouveau</span>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Rechercher..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12 sm:h-14 text-base" data-testid="doc-search" />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="h-12 sm:h-14 w-[130px] sm:w-44 flex-shrink-0" data-testid="doc-filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent position="popper" className="z-[100]">
              <SelectItem value="all" className="py-3">Tous les types</SelectItem>
              {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="py-3">{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterChantier} onValueChange={setFilterChantier}>
            <SelectTrigger className="h-12 sm:h-14 w-[140px] sm:w-48 flex-shrink-0" data-testid="doc-filter-chantier">
              <SelectValue placeholder="Chantier" />
            </SelectTrigger>
            <SelectContent position="popper" className="z-[100]">
              <SelectItem value="all" className="py-3">Tous</SelectItem>
              {(chantiers || []).filter(c => c && c.id && c.name).map(c => (
                <SelectItem key={c.id} value={String(c.id)} className="py-3">{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-12 sm:h-14 w-[130px] sm:w-40 flex-shrink-0" data-testid="doc-filter-status">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent position="popper" className="z-[100]">
              <SelectItem value="all" className="py-3">Tous</SelectItem>
              <SelectItem value="brouillon" className="py-3">Brouillon</SelectItem>
              <SelectItem value="en_cours" className="py-3">En cours</SelectItem>
              <SelectItem value="valide" className="py-3">Valide</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Cards */}
      {documents.length === 0 ? (
        <Card className="border-dashed border-2 mt-3">
          <CardContent className="py-12 sm:py-16 text-center">
            <FileText size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-base font-medium text-[#111827]">Aucun document</p>
            <p className="text-sm text-[#4B5563] mt-1">
              {chantiers.length === 0 ? "Creez d'abord un chantier CSPS" : 'Généréz vos documents depuis un template'}
            </p>
            {chantiers.length > 0 && (
              <Button className="bg-[#FF6B00] hover:bg-orange-700 mt-4" onClick={onOpenWizard} data-testid="empty-new-doc-btn">
                <MagicWand size={20} className="mr-2" /> Générer un document
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3 mt-3">
          {documents.map(d => {
            const hasContenu = !!d.contenu;
            const cfg = STATUS_CONFIG[d.status] || STATUS_CONFIG.brouillon;
            const typeInfo = DOC_TYPES.find(t => t.value === d.type_document);
            return (
              <Card key={d.id} className="border hover:border-[#FF6B00]/30 transition-colors active:bg-gray-50"
                data-testid={`doc-card-${d.id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                      <FileText size={22} className="text-[#FF6B00]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm sm:text-base text-[#111827] truncate">{d.nom}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-bold flex-shrink-0 ${cfg.badge}`}>
                          {d.status === 'valide' && <LockSimple size={10} weight="fill" className="inline mr-0.5 -mt-0.5" />}
                          {cfg.label}
                        </span>
                        <span className="px-2 py-0.5 rounded text-xs font-semibold bg-orange-100 text-orange-700 uppercase flex-shrink-0">
                          {typeInfo?.label || d.type_document}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-[#4B5563] mt-0.5 truncate">{getChantierName(d.chantier_id)}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-[#6B7280]">
                        <span>V{d.version}</span>
                        {d.entreprise && <span className="font-medium">{d.entreprise}</span>}
                        {d.created_at && <span>{new Date(d.created_at).toLocaleDateString('fr-FR')}</span>}
                        {(d.versions || []).length > 0 && (
                          <span className="flex items-center gap-0.5"><ClockCounterClockwise size={12} /> {d.versions.length}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-3 flex-wrap">
                    {hasContenu && (
                      <Button variant="outline" size="sm" onClick={() => onOpenView(d)}
                        className="h-10 flex-1 sm:flex-none text-sm" data-testid={`view-doc-${d.id}`}>
                        <Eye size={16} className="mr-1.5" /> Voir
                      </Button>
                    )}
                    {d.status === 'valide' && hasContenu && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => onDownloadPdf(d)}
                          className="h-10 text-[#FF6B00]" data-testid={`pdf-doc-${d.id}`}>
                          <FilePdf size={16} className="mr-1" /> PDF
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => onDownloadDocx(d)}
                          className="h-10 text-blue-600" data-testid={`docx-doc-${d.id}`}>
                          <FileDoc size={16} className="mr-1" /> Word
                        </Button>
                        {onSendEmail && (
                          <Button variant="outline" size="sm" onClick={() => onSendEmail(d)}
                            className="h-10 text-emerald-700 border-emerald-300" data-testid={`email-doc-${d.id}`}>
                            <span className="mr-1">✉️</span> Envoyer
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="h-10 text-blue-600"
                          onClick={() => onNewVersion(d)} data-testid={`newversion-doc-${d.id}`}>
                          <ArrowCounterClockwise size={16} className="mr-1" /> V{parseInt(parseFloat(d.version)) + 1}
                        </Button>
                      </>
                    )}
                    {d.status !== 'valide' && hasContenu && (
                      <Button variant="outline" size="sm" className="h-10 text-emerald-600 border-emerald-200"
                        onClick={() => onValidate(d)} data-testid={`validate-doc-${d.id}`}>
                        <SealCheck size={16} className="mr-1" /> Valider
                      </Button>
                    )}
                    {d.status !== 'valide' && (
                      <Button variant="outline" size="icon" onClick={() => onDeleteConfirm(d)}
                        className="h-10 w-10 text-red-600 hover:bg-red-50 flex-shrink-0" data-testid={`delete-doc-${d.id}`}>
                        <Trash size={16} />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
